import React, { useState } from 'react';
import { Alert, Button, Checkbox, Empty, Select, Spin, Table, Tag, Typography } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { GAP_ONLY_FILTER_LABEL, coverageStatusLabel } from '../../utils/libraryTemplateModel';

const { Text } = Typography;

const STATUS_COLOURS = { none: 'red', draft: 'default', in_review: 'gold', approved: 'blue', published: 'green', retired: 'default' };

/**
 * Coverage view (GET /admin/library-templates/coverage?standardId=): every control in the
 * current Policy Hub master catalogue with its template state. A control with no template is
 * shown as a gap, never hidden — partial coverage must be visible, not papered over
 * (library-curator.md §0/§7). The total, published and gap counts come from the API.
 * platform_admin only (server-enforced).
 */
const LibraryCoveragePanel = ({ standardOptions, onOpenItem, onDraftWithAi }) => {
    const { request } = useApiRequest();
    const [standardId, setStandardId] = useState(null);
    const [gapsOnly, setGapsOnly] = useState(false);

    const coverage = useResource(
        async () => (await request('GET', '/admin/library-templates/coverage', { query: { standardId } })).data,
        [standardId, request],
        { enabled: !!standardId },
    );
    const rows = coverage.data?.rows || [];
    const shown = gapsOnly ? rows.filter((r) => r.templateStatus !== 'published') : rows;
    const summary = coverage.data?.summary;

    const columns = [
        { title: 'Control', dataIndex: 'controlRef', key: 'controlRef', width: 140, render: (c) => <Text strong>{c}</Text> },
        { title: 'Name', dataIndex: 'controlName', key: 'controlName' },
        { title: 'Family', dataIndex: 'family', key: 'family', width: 140, render: (f) => f || '-' },
        {
            title: 'Template',
            key: 'status',
            width: 200,
            render: (_, r) => (
                <div>
                    <Tag color={STATUS_COLOURS[r.templateStatus] || 'default'}>{coverageStatusLabel(r.templateStatus)}</Tag>
                    {r.currentVersion ? <Text type="secondary">v{r.currentVersion}</Text> : r.latestVersion ? <Text type="secondary">latest v{r.latestVersion}</Text> : null}
                    {r.aiGenerated && <Tag color="orange">AI</Tag>}
                </div>
            ),
        },
        {
            title: '',
            key: 'actions',
            width: 160,
            render: (_, r) =>
                r.latestItemId ? (
                    <Button size="small" onClick={() => onOpenItem(r.latestItemId)}>Open</Button>
                ) : (
                    <Button size="small" type="primary" onClick={() => onDraftWithAi(standardId, r.controlRef)}>Draft with AI</Button>
                ),
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                <Select
                    aria-label="Coverage standard"
                    showSearch
                    optionFilterProp="label"
                    style={{ minWidth: 280 }}
                    value={standardId}
                    onChange={setStandardId}
                    options={standardOptions}
                    placeholder="Choose a standard"
                />
                {standardId && (
                    <Checkbox checked={gapsOnly} onChange={(e) => setGapsOnly(e.target.checked)}>{GAP_ONLY_FILTER_LABEL}</Checkbox>
                )}
            </div>

            {!standardId && <Empty description="Choose a standard to see which of its controls have a published library template." />}
            {standardId && coverage.status === 'loading' && !coverage.data && <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>}
            {standardId && coverage.status === 'error' && (
                <Alert type="error" showIcon message="Could not load coverage" description={coverage.error?.message} action={<Button size="small" onClick={coverage.reload}>Retry</Button>} />
            )}
            {standardId && coverage.status === 'ready' && summary && (
                <Alert
                    type={summary.published === summary.totalControls ? 'success' : 'warning'}
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`${summary.published} of ${summary.totalControls} controls have a published template; ${summary.totalControls - summary.published} do not (${summary.withoutTemplate} have no template at all; the rest are draft, in review, approved or retired).`}
                />
            )}
            {standardId && coverage.status === 'ready' && rows.length === 0 && (
                <Empty description="The Policy Hub has no controls loaded for this standard yet." />
            )}
            {rows.length > 0 && coverage.status !== 'error' && (
                <Table rowKey={(r) => r.controlRef} size="small" columns={columns} dataSource={shown} pagination={{ pageSize: 25, hideOnSinglePage: true }} loading={coverage.status === 'loading'} />
            )}
        </div>
    );
};

export default LibraryCoveragePanel;
